import { ComponentType, ReactNode, useMemo, useRef, useState } from 'react';
import Dialog from '#shared/components/Dialog/Dialog';
import { DialogComponentProps, DialogContext, DialogOptions, DialogService } from './DialogContext';

type DialogEntry = {
  id: number;
  content: ReactNode;
  options: DialogOptions;
  isOpen: boolean;
  cancel: () => void;
  exit: () => void;
};

// How far below the top of the stack a dialog sits — Dialog.module.scss turns it into a scale and a dim.
// A settled dialog counts as the top one: it is on its way out and shouldn't step back.
function depthFromTop(openIds: number[], id: number) {
  const position = openIds.indexOf(id);

  if (position === -1) return 0;

  return openIds.length - 1 - position;
}

// Shared owns the dialog UI: the content renders here, under every shared context, so a dialog
// component can call useApi()/useAlertDialog()/etc. without any re-wrapping.
//
// Dialogs stack — open() mounts a new one on top of whatever is already showing, so a dialog can open
// another and closing that one returns to the first. Radix does the layering: its DismissableLayer keeps
// a global layer stack, so Escape and outside clicks only reach the topmost dialog and the ones
// underneath go inert, and FocusScope traps focus in the newest layer. What Radix can't do here is put
// focus back on close — its restore focuses a Dialog.Trigger, which an imperatively-opened dialog has
// none of — so open() remembers what was focused and exit() returns focus to it. Dialog.Content is not portaled, so
// entries are DOM siblings in push order and later ones paint above earlier ones at the same --zModal.
// The stacked look is ours, not Radix's: only the bottom entry paints an overlay, so the backdrop dims
// once however deep the stack goes, and the dialogs under the top one step back and dim by depth.
export default function DialogProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<DialogEntry[]>([]);
  const nextIdRef = useRef(0);

  function open<ReturnType>(Component: ComponentType<DialogComponentProps<ReturnType>>, opts: DialogOptions = {}) {
    return new Promise<ReturnType | null>((resolve) => {
      const id = nextIdRef.current++;

      // Where focus was when open() was called: the button in the page, or the one in the dialog
      // underneath for a stacked dialog. exit() puts focus back there once this dialog is gone.
      const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      // Settling resolves the caller and flips this entry closed, which is what lets Radix play the exit
      // animation. The entry is dropped later, by exit(), once Radix has unmounted the content — dropping
      // it here would unmount the whole <Dialog> and skip the animation.
      const settle = (value: ReturnType | null) => {
        setStack((prev) => prev.map((entry) => (entry.id === id ? { ...entry, isOpen: false } : entry)));
        resolve(value);
      };

      // Settles this dialog with null: the component's own onCancel, and the Radix close paths
      // (Escape / outside click), which only fire for the topmost dialog.
      const cancel = () => settle(null);

      // Restoring focus is ours to do, not Radix's: Dialog.Content suppresses Radix's close-focus
      // handler, whose only move is focusing a Dialog.Trigger that an imperatively-opened dialog
      // doesn't have. By the time exit() runs the content is unmounted and the layer below has
      // resumed its focus trap, so focusing the opener sticks. An opener that has since left the DOM
      // needs no special case — focus() on a detached element does nothing.
      const exit = () => {
        opener?.focus();

        setStack((prev) => prev.filter((entry) => entry.id !== id));
      };

      setStack((prev) => [
        ...prev,
        { id, options: opts, isOpen: true, cancel, exit, content: <Component onConfirm={settle} onCancel={cancel} /> },
      ]);
    });
  }

  // Built once: open() only closes over setStack and nextIdRef, both stable, so consumers get one
  // service identity for the provider's lifetime (see AlertDialog.provider).
  const service = useMemo<DialogService>(() => ({ open }), []);

  // Depth is measured among the dialogs still open, so one that is closing stops holding the dialogs
  // below it stepped back — they scale up again as it fades out.
  const openIds = stack.filter((entry) => entry.isOpen).map((entry) => entry.id);

  return (
    <DialogContext.Provider value={service}>
      {children}

      {stack.map((entry, index) => (
        <Dialog key={entry.id} isOpen={entry.isOpen} onClose={entry.cancel}>
          <Dialog.Content
            {...entry.options}
            // The bottom entry owns the single overlay, rather than the top one, so it mounts when the
            // stack fills and unmounts with the last dialog's own exit animation instead of being torn
            // down and rebuilt on every push.
            overlay={index === 0}
            depthFromTop={depthFromTop(openIds, entry.id)}
            onExited={entry.exit}
          >
            {entry.content}
          </Dialog.Content>
        </Dialog>
      ))}
    </DialogContext.Provider>
  );
}
